import { Controller } from '@hotwired/stimulus';
import { reconcileVatBases } from '../utils/vat_reconcile';

// Taux proposés, dans l'ordre du menu « Taux de TVA ».
const VAT_RATES = ['20', '10', '5.5'];

// Controller pour calculer automatiquement la TVA et le montant TTC
// Gère l'ajout/suppression dynamique de lignes de TVA
export default class extends Controller {
  static targets = [
    'baseAmountHt', // Montant de base HT
    'baseAmountTtc', // Hidden field pour base_amount_ttc
    'vatLinesContainer', // Container des lignes de TVA
    'addVatButton', // Bouton pour ajouter une ligne
    'summaryHt', // Affichage montant HT dans récap
    'summaryVatDetails', // Container pour détails TVA dans récap
    'summaryTtc', // Affichage montant TTC dans récap
    'warningMessage', // Message d'avertissement si incohérence
    'negativeVatWarning', // Warning TS négatif avec taux croisé
    'vat20Active', // Hidden field pour vat_20_active
    'vat10Active', // Hidden field pour vat_10_active
    'vat55Active', // Hidden field pour vat_5_5_active
    'amount20', // Hidden field pour amount_vat_20
    'amount10', // Hidden field pour amount_vat_10
    'amount55', // Hidden field pour amount_vat_5_5
    'vatBadge', // Badges de sélection de mode TVA (5.5%, 10%, 20%, Multi-TVA)
    'vatMode', // Hidden field pour stocker le mode sélectionné
    'vatExempt', // Hidden field pour vat_exempt (franchise art. 293 B)
    'franchiseHelp', // Texte d'aide légal franchise
    'vatDistribution', // Bloc "Répartition par taux" masqué en franchise
    'inputModeToggle', // Radios du switch Base HT / TVA
    'inputModeSwitch', // Wrapper du switch (masqué en franchise)
    'tva55', // Hidden field TVA saisie 5,5 % (mode TVA, recalcul serveur)
    'tva10', // Hidden field TVA saisie 10 %
    'tva20', // Hidden field TVA saisie 20 %
  ];

  static values = {
    contractVatRates: { type: Array, default: [] }, // Taux actifs sur le marché de base
  };

  connect() {
    // Détecter le mode TVA au chargement (pour l'édition)
    this.detectVatMode();
    this.updateCalculations();
  }

  // Mode de saisie courant : 'ht' (assiette HT, défaut) ou 'tva' (montant de TVA).
  get inputMode() {
    if (!this.hasInputModeToggleTarget) return 'ht';
    const checked = this.inputModeToggleTargets.find((radio) => radio.checked);
    return checked ? checked.value : 'ht';
  }

  // Saisie TVA active : on tape la TVA (le HT est dérivé). Toujours faux en
  // franchise (aucune TVA à saisir). Source unique du prédicat dupliqué ailleurs.
  get tvaMode() {
    const mode = this.hasVatModeTarget ? this.vatModeTarget.value : null;
    return mode !== 'franchise' && this.inputMode === 'tva';
  }

  // Force le switch sur "Base HT" (ex. à l'entrée en franchise).
  forceInputModeHt() {
    if (!this.hasInputModeToggleTarget) return;
    this.inputModeToggleTargets.forEach((radio) => {
      radio.checked = radio.value === 'ht';
    });
  }

  // Bascule du switch : reset des montants, reconstruction SANS perte d'état,
  // puis recalcul.
  setInputMode() {
    const mode = this.hasVatModeTarget ? this.vatModeTarget.value : null;
    this.baseAmountHtTarget.value = '';
    this.baseAmountHtTarget.dataset.tvaAuto = '1';

    if (mode === 'multi') {
      // Préserver les taux ET le nombre de lignes ; reconstruire avec montants
      // vides + labels/éditabilité du mode courant (ne PAS retomber sur 10/20).
      const rates = Array.from(
        this.element.querySelectorAll('[data-vat-line] [data-vat-rate]')
      ).map((select) => select.value);
      this.vatLinesContainerTarget.innerHTML = '';
      rates.forEach((rate) => {
        const tpl = this.createVatLineTemplate(rate || null, null, false);
        this.vatLinesContainerTarget.insertAdjacentHTML('beforeend', tpl);
      });
      this.updateUIForMode();
      this.updateCalculations();
    } else if (mode === '5.5' || mode === '10' || mode === '20') {
      // Mono-taux : une seule ligne, taux fixé par le badge → reconstruction sûre.
      this.applyVatMode(mode);
    } else {
      this.updateCalculations();
    }
  }

  // Saisie dans "Montant de base HT". En mode TVA, le champ reste éditable : vide,
  // il suit la somme des bases déduites (auto) ; saisi, il sert de cible pour
  // absorber l'écart d'arrondi (cf. utils/vat_reconcile.js).
  baseAmountInput() {
    if (this.tvaMode) {
      this.baseAmountHtTarget.dataset.tvaAuto = this.baseAmountHtTarget.value === '' ? '1' : '0';
    }
    this.updateCalculations();
  }

  // Vidé puis quitté : on réaffiche la somme des bases déduites.
  baseAmountBlur() {
    if (this.tvaMode && this.baseAmountHtTarget.value === '') this.updateCalculations();
  }

  // HT saisi par l'utilisateur en mode TVA, ou null s'il suit la somme déduite.
  get tvaTargetHt() {
    const el = this.baseAmountHtTarget;
    if (el.dataset.tvaAuto !== '0' || el.value === '') return null;
    const value = parseFloat(el.value);
    return Number.isNaN(value) ? null : value;
  }

  // Détecte le mode TVA au chargement (pour l'édition d'un marché existant)
  detectVatMode() {
    // Édition d'un marché/TS déjà en franchise : activer le mode au chargement.
    if (this.hasVatExemptTarget && this.vatExemptTarget.value === '1') {
      const franchiseBadge = this.hasVatBadgeTarget
        ? this.vatBadgeTargets.find((b) => b.dataset.vatMode === 'franchise')
        : null;
      if (franchiseBadge) {
        this.activateBadge(franchiseBadge);
        this.vatModeTarget.value = 'franchise';
      }
      this.updateUIForMode();
      return;
    }

    if (!this.hasVatBadgeTarget) return;

    const existingLines = this.element.querySelectorAll('[data-vat-line]');

    // Si aucune ligne existante, ne rien faire (mode sera sélectionné par l'utilisateur)
    if (existingLines.length === 0) return;

    // Compter combien de taux différents sont actifs
    const activeRates = new Set();
    existingLines.forEach((line) => {
      const rateSelect = line.querySelector('[data-vat-rate]');
      if (rateSelect && rateSelect.value) {
        activeRates.add(rateSelect.value);
      }
    });

    // Si un seul taux actif, sélectionner le badge correspondant
    if (activeRates.size === 1) {
      const rate = Array.from(activeRates)[0];
      const badge = this.vatBadgeTargets.find((b) => b.dataset.vatMode === rate);
      if (badge) {
        this.activateBadge(badge);
        this.vatModeTarget.value = rate;
      }
    } else if (activeRates.size > 1) {
      // Si plusieurs taux, sélectionner "Multi-TVA"
      const multiBadge = this.vatBadgeTargets.find((b) => b.dataset.vatMode === 'multi');
      if (multiBadge) {
        this.activateBadge(multiBadge);
        this.vatModeTarget.value = 'multi';
      }
    }

    // Adapter l'UI selon le mode détecté
    this.updateUIForMode();
  }

  // Méthode appelée quand on clique sur un badge de mode TVA
  selectVatMode(event) {
    const badge = event.currentTarget;
    const mode = badge.dataset.vatMode;

    // Si on clique sur le badge déjà sélectionné, ne rien faire
    if (this.vatModeTarget.value === mode) return;

    // Activer visuellement le badge
    this.activateBadge(badge);

    // Stocker le mode sélectionné
    this.vatModeTarget.value = mode;

    // Appliquer le mode (créer les lignes de TVA selon le mode)
    this.applyVatMode(mode);
  }

  // Active visuellement un badge et désactive les autres
  activateBadge(activeBadge) {
    this.vatBadgeTargets.forEach((badge) => {
      const isFranchise = badge.dataset.vatMode === 'franchise';
      if (badge === activeBadge) {
        // Style du badge actif
        badge.classList.remove('border-neutral-300', 'text-neutral-700', 'bg-white');
        if (isFranchise) {
          // Tonalité info, distincte des taux (primary).
          badge.classList.add('border-info-500', 'bg-info-50', 'text-info-700');
        } else {
          badge.classList.add('border-primary-600', 'text-white', 'bg-primary-600');
        }
      } else {
        // Style du badge inactif
        badge.classList.remove(
          'border-primary-600',
          'text-white',
          'bg-primary-600',
          'border-info-500',
          'bg-info-50',
          'text-info-700'
        );
        badge.classList.add('border-neutral-300', 'text-neutral-700', 'bg-white');
      }
    });
  }

  // Applique le mode TVA sélectionné (crée les lignes appropriées)
  applyVatMode(mode) {
    const baseAmount = parseFloat(this.baseAmountHtTarget.value) || 0;

    // Vider le container de lignes existantes
    this.vatLinesContainerTarget.innerHTML = '';

    if (mode === 'franchise') {
      // Franchise art. 293 B : aucune ligne de TVA, TTC = HT.
    } else if (mode === '5.5' || mode === '10' || mode === '20') {
      // Mode taux unique : une ligne. En saisie HT, pré-remplie (miroir du
      // montant de base) ; en saisie TVA, laissée vide (l'utilisateur tape la TVA).
      const prefill = this.inputMode === 'tva' ? null : baseAmount;
      const line = this.createVatLineTemplate(mode, prefill, true);
      this.vatLinesContainerTarget.insertAdjacentHTML('beforeend', line);
    } else if (mode === 'multi') {
      // Mode Multi-TVA : créer deux lignes (10% et 20%) avec montants à 0
      const line1 = this.createVatLineTemplate('10', 0, false);
      const line2 = this.createVatLineTemplate('20', 0, false);
      this.vatLinesContainerTarget.insertAdjacentHTML('beforeend', line1);
      this.vatLinesContainerTarget.insertAdjacentHTML('beforeend', line2);
    }

    // Mettre à jour l'UI selon le mode
    this.updateUIForMode();

    // Recalculer les montants
    this.updateCalculations();
  }

  // Met à jour l'interface selon le mode sélectionné
  updateUIForMode() {
    const mode = this.vatModeTarget.value;
    const isFranchise = mode === 'franchise';

    // Bloc "Répartition par taux" : masqué en franchise.
    if (this.hasVatDistributionTarget) {
      this.vatDistributionTarget.classList.toggle('hidden', isFranchise);
    }
    // Texte d'aide légal : affiché en franchise.
    if (this.hasFranchiseHelpTarget) {
      this.franchiseHelpTarget.classList.toggle('hidden', !isFranchise);
    }
    // Switch Base HT / TVA : affiché seulement quand un taux taxable est
    // sélectionné (5,5 / 10 / 20 / multi). Masqué tant que rien n'est choisi
    // et en franchise (pas de TVA à saisir) ; en franchise on le remet aussi
    // sur "Base HT" pour ne pas rester coincé en mode TVA.
    const isRealRate = mode === '5.5' || mode === '10' || mode === '20' || mode === 'multi';
    if (this.hasInputModeSwitchTarget) {
      this.inputModeSwitchTarget.classList.toggle('hidden', !isRealRate);
    }
    if (isFranchise) this.forceInputModeHt();

    if (isFranchise || mode === '5.5' || mode === '10' || mode === '20') {
      // Franchise et mode taux unique : masquer le bouton "Ajouter un taux"
      // Note: style.display used instead of hidden class because Tailwind v4's
      // inline-flex (from ButtonComponent) overrides hidden in the utility layer
      if (this.hasAddVatButtonTarget) {
        this.addVatButtonTarget.style.display = 'none';
      }
    } else if (mode === 'multi') {
      // Mode Multi-TVA : afficher le bouton "Ajouter un taux"
      if (this.hasAddVatButtonTarget) {
        this.addVatButtonTarget.style.display = '';
      }
    }
  }

  // Méthode appelée quand on clique sur "+ Ajouter un taux"
  addVatLine() {
    const mode = this.vatModeTarget.value;
    if (mode === '5.5' || mode === '10' || mode === '20') return;

    // Nouvelle ligne directement sur le premier taux libre (un taux par ligne).
    const freeRate = VAT_RATES.find((rate) => !this.usedRates().includes(rate));
    if (!freeRate) return;

    const template = this.createVatLineTemplate(freeRate);
    this.vatLinesContainerTarget.insertAdjacentHTML('beforeend', template);
    this.updateCalculations();
  }

  // Taux sélectionnés sur les lignes de répartition.
  usedRates(exceptSelect = null) {
    return Array.from(this.element.querySelectorAll('[data-vat-line] [data-vat-rate]'))
      .filter((select) => select !== exceptSelect && select.value)
      .map((select) => select.value);
  }

  // Multi-TVA : un taux ne peut figurer que sur une ligne. Les taux pris par une
  // autre ligne sont grisés dans chaque menu, et « Ajouter un taux » disparaît
  // quand les trois sont utilisés. (Les montants sont stockés une fois par taux :
  // deux lignes au même taux ne survivraient pas à l'enregistrement.)
  refreshRateOptions() {
    if (!this.hasVatModeTarget || this.vatModeTarget.value !== 'multi') return;

    this.element.querySelectorAll('[data-vat-line] [data-vat-rate]').forEach((select) => {
      const takenElsewhere = this.usedRates(select);
      Array.from(select.options).forEach((option) => {
        if (option.value) option.disabled = takenElsewhere.includes(option.value);
      });
    });

    if (this.hasAddVatButtonTarget) {
      const allUsed = VAT_RATES.every((rate) => this.usedRates().includes(rate));
      this.addVatButtonTarget.style.display = allUsed ? 'none' : '';
    }
  }

  // Méthode appelée quand on clique sur le bouton supprimer d'une ligne
  removeVatLine(event) {
    const mode = this.vatModeTarget.value;

    // En mode taux unique, empêcher la suppression
    if (mode === '5.5' || mode === '10' || mode === '20') {
      return;
    }

    const line = event.target.closest('[data-vat-line]');
    if (line) {
      line.remove();
      this.updateCalculations();
    }
  }

  // Méthode appelée à chaque changement dans les inputs
  updateCalculations() {
    const mode = this.hasVatModeTarget ? this.vatModeTarget.value : null;
    const tvaMode = this.tvaMode;

    // Franchise art. 293 B : TTC = HT, aucun bucket actif, vat_exempt = 1.
    if (mode === 'franchise') {
      const ht = parseFloat(this.baseAmountHtTarget.value) || 0;
      if (this.hasVatExemptTarget) this.vatExemptTarget.value = '1';
      this.syncHiddenFields([]); // remet tous les buckets à 0/false
      this.updateSummary(ht, [], ht); // récap HT + TTC = HT
      this.syncTvaFields([]);
      return;
    }
    if (this.hasVatExemptTarget) this.vatExemptTarget.value = '0';

    const baseAmountHt = parseFloat(this.baseAmountHtTarget.value) || 0;

    // Mode taux unique EN SAISIE HT : la ligne reflète (readonly) le montant de base.
    // En saisie TVA, la ligne est l'entrée et le HT de base est dérivé → on saute ce bloc.
    if (!tvaMode && (mode === '5.5' || mode === '10' || mode === '20')) {
      const singleLine = this.element.querySelector('[data-vat-line]');
      if (singleLine) {
        const amountInput = singleLine.querySelector('[data-vat-amount]');
        if (amountInput && parseFloat(amountInput.value) !== baseAmountHt) {
          amountInput.value = baseAmountHt;
        }
        if (amountInput) {
          amountInput.readOnly = true;
          amountInput.classList.add('bg-neutral-100', 'text-neutral-500', 'cursor-not-allowed');
          amountInput.classList.remove(
            'bg-white',
            'focus-visible:ring-2',
            'focus-visible:ring-primary-500',
            'focus-visible:border-primary-500'
          );
        }
      }
    }

    // Collecter toutes les lignes de TVA (conversion TVA→HT en mode TVA).
    const vatLines = this.collectVatLines();

    // Mettre à jour les champs cachés (toujours en HT ; la TVA saisie est postée
    // à part pour que le serveur refasse la déduction).
    this.syncHiddenFields(vatLines);
    this.syncTvaFields(tvaMode ? vatLines : []);

    const totalHtByVat = vatLines.reduce((sum, line) => sum + line.amountHt, 0);
    const totalVat = vatLines.reduce((sum, line) => sum + line.vatAmount, 0);
    const totalTtc = totalHtByVat + totalVat;

    if (tvaMode) {
      // Base HT non saisie : elle suit la somme des bases déduites (sauf pendant
      // que l'utilisateur la vide, pour ne pas écraser sa frappe).
      const el = this.baseAmountHtTarget;
      if (this.tvaTargetHt === null && !(el.value === '' && document.activeElement === el)) {
        el.dataset.tvaAuto = '1';
        el.value = totalHtByVat.toFixed(2);
      }
    }

    this.updateSummary(totalHtByVat, vatLines, totalTtc);

    if (tvaMode) {
      this.checkTvaConsistency(this.lastReconcile);
    } else {
      this.checkConsistency(baseAmountHt, totalHtByVat);
    }
    const consistencyBase = tvaMode ? totalHtByVat : baseAmountHt;
    this.checkCrossRateWarning(consistencyBase, vatLines);
    this.refreshRateOptions();
  }

  // Collecte toutes les lignes de TVA et calcule les montants
  collectVatLines() {
    const lines = [];
    const lineElements = this.element.querySelectorAll('[data-vat-line]');
    const mode = this.hasVatModeTarget ? this.vatModeTarget.value : null;
    const isSingleRate = mode === '5.5' || mode === '10' || mode === '20';
    const tvaMode = this.tvaMode;

    // Mode TVA : bases déduites de la TVA saisie, écart d'arrondi absorbé vers
    // le HT saisi (si atteignable). Une base par ligne, dans l'ordre du DOM.
    let tvaBases = new Map();
    this.lastReconcile = null;
    if (tvaMode) {
      const typedLines = [];
      lineElements.forEach((lineEl) => {
        const rate = this.lineRate(lineEl, mode, isSingleRate);
        const amountInput = lineEl.querySelector('[data-vat-amount]');
        const tva = amountInput ? parseFloat(amountInput.value) || 0 : 0;
        if (rate > 0 && tva !== 0) typedLines.push({ lineEl, rate, tva });
      });
      this.lastReconcile = reconcileVatBases(typedLines, this.tvaTargetHt);
      tvaBases = new Map(typedLines.map((line, i) => [line.lineEl, this.lastReconcile.bases[i]]));
    }

    lineElements.forEach((lineEl) => {
      const rateSelect = lineEl.querySelector('[data-vat-rate]');
      const amountInput = lineEl.querySelector('[data-vat-amount]');

      if (rateSelect && amountInput) {
        const rate = this.lineRate(lineEl, mode, isSingleRate);

        const typed = parseFloat(amountInput.value) || 0;
        let amountHt;
        let vatAmount;
        if (tvaMode && rate > 0) {
          // La valeur saisie EST la TVA ; la base vient de la réconciliation
          // (round(base × taux) = TVA par construction).
          amountHt = tvaBases.get(lineEl) ?? 0;
          vatAmount = typed;
        } else {
          amountHt = typed;
          vatAmount = Math.round(amountHt * (rate / 100) * 100) / 100;
        }

        // Accepter les montants négatifs (pour TS négatifs/régularisations)
        if (amountHt != 0 && rate > 0) {
          lines.push({
            rate: rate,
            amountHt: amountHt,
            vatAmount: vatAmount,
          });
        }

        // Mettre à jour l'affichage sous la ligne (HT déduit en TVA, TVA calculée en HT)
        const calculatedDisplay = lineEl.querySelector('[data-vat-calculated]');
        if (calculatedDisplay) {
          if (typed != 0 && rate > 0) {
            calculatedDisplay.textContent = tvaMode
              ? `Base HT déduite (${rate}%) : ${this.formatCurrency(amountHt)}`
              : `TVA calculée (${rate}%) : ${this.formatCurrency(vatAmount)}`;
            calculatedDisplay.classList.remove('invisible');
          } else {
            calculatedDisplay.classList.add('invisible');
          }
        }
      }
    });

    return lines;
  }

  // Taux d'une ligne. En mode taux unique avec select désactivé, le mode est la
  // source fiable (un <select disabled> peut retourner une valeur vide).
  lineRate(lineEl, mode, isSingleRate) {
    const rateSelect = lineEl.querySelector('[data-vat-rate]');
    if (!rateSelect) return 0;
    if (isSingleRate && rateSelect.disabled) return parseFloat(mode);
    return parseFloat(rateSelect.value) || 0;
  }

  // Mode TVA : alerte si le HT saisi n'est pas atteignable par simple arrondi
  // (un écart d'arrondi, lui, est absorbé sans mention).
  checkTvaConsistency(result) {
    if (!this.hasWarningMessageTarget) return;

    if (result && !result.reachable) {
      const typedHt = this.tvaTargetHt ?? result.deducedHt;
      this.warningMessageTarget.classList.remove('hidden');
      this.warningMessageTarget.querySelector('[data-warning-text]').textContent =
        `Les bases déduites de la TVA (${this.formatCurrency(result.deducedHt)}) ne correspondent pas ` +
        `au montant HT saisi (${this.formatCurrency(typedHt)}), écart ${this.formatCurrency(Math.abs(result.gap))}. ` +
        'Vérifiez les montants de TVA ou le montant HT.';
    } else {
      this.warningMessageTarget.classList.add('hidden');
    }
  }

  // TVA saisie par taux (mode TVA) → champs cachés lus par le serveur ; vides sinon.
  syncTvaFields(vatLines) {
    const totals = { 5.5: 0, 10: 0, 20: 0 };
    vatLines.forEach((line) => {
      if (line.rate in totals) totals[line.rate] += line.vatAmount;
    });
    const fields = [
      [this.hasTva55Target, 'tva55Target', 5.5],
      [this.hasTva10Target, 'tva10Target', 10],
      [this.hasTva20Target, 'tva20Target', 20],
    ];
    fields.forEach(([present, target, rate]) => {
      if (present) this[target].value = totals[rate] !== 0 ? totals[rate].toFixed(2) : '';
    });
  }

  // Met à jour le récapitulatif visuel
  updateSummary(totalHt, vatLines, totalTtc) {
    // Montant HT
    if (this.hasSummaryHtTarget) {
      this.summaryHtTarget.textContent = this.formatCurrency(totalHt);
    }

    // Détails TVA
    if (this.hasSummaryVatDetailsTarget) {
      let vatDetailsHtml = '';
      vatLines.forEach((line, index) => {
        const id = `vat-rate-calculation-label-${index}`;
        vatDetailsHtml += `
          <div class="flex justify-between text-sm" data-test="vat-summary-row">
            <span class="text-neutral-600" id="${id}">+ TVA ${line.rate}% (sur ${this.formatCurrency(line.amountHt)})</span>
            <span class="text-neutral-900 font-medium" aria-labelledby="${id}">${this.formatCurrency(line.vatAmount)}</span>
          </div>
        `;
      });
      this.summaryVatDetailsTarget.innerHTML = vatDetailsHtml;
    }

    // Montant TTC (affichage et champ caché)
    if (this.hasSummaryTtcTarget) {
      this.summaryTtcTarget.textContent = this.formatCurrency(totalTtc);
    }

    // Mettre à jour le champ caché base_amount_ttc pour la soumission du formulaire
    if (this.hasBaseAmountTtcTarget) {
      this.baseAmountTtcTarget.value = totalTtc.toFixed(2);
    }
  }

  // Vérifie la cohérence entre montant de base et somme des montants par taux
  checkConsistency(baseAmount, totalByVat) {
    if (!this.hasWarningMessageTarget) return;

    const difference = Math.abs(baseAmount - totalByVat);

    // Tolérance de 0.01€ pour les arrondis
    // Vérifier la cohérence même pour montants négatifs (baseAmount != 0)
    if (difference > 0.01 && baseAmount != 0) {
      this.warningMessageTarget.classList.remove('hidden');
      const diff = baseAmount - totalByVat;
      const message =
        diff > 0
          ? `Il manque ${this.formatCurrency(Math.abs(diff))} à répartir`
          : `Vous avez réparti ${this.formatCurrency(Math.abs(diff))} de trop`;
      this.warningMessageTarget.querySelector('[data-warning-text]').textContent = message;
    } else {
      this.warningMessageTarget.classList.add('hidden');
    }
  }

  // Vérifie si un TS négatif utilise un taux absent du marché et affiche un warning non-bloquant
  checkCrossRateWarning(baseAmountHt, vatLines) {
    if (!this.hasNegativeVatWarningTarget) return;
    if (this.contractVatRatesValue.length === 0) return;

    const isNegative = baseAmountHt < 0;
    if (!isNegative) {
      this.negativeVatWarningTarget.classList.add('hidden');
      return;
    }

    const hasCrossRate = vatLines.some(
      (line) => !this.contractVatRatesValue.includes(String(line.rate))
    );
    this.negativeVatWarningTarget.classList.toggle('hidden', !hasCrossRate);
  }

  // Template HTML pour une nouvelle ligne de TVA.
  // Les ids des éléments sont générés via crypto.randomUUID() dans le corps.
  // Params:
  // - preselectedRate: taux pré-sélectionné (optionnel)
  // - prefilledAmount: montant pré-rempli (optionnel)
  // - hideDeleteButton: masquer le bouton supprimer (pour mode taux unique)
  createVatLineTemplate(preselectedRate = null, prefilledAmount = null, hideDeleteButton = false) {
    const mode = this.hasVatModeTarget ? this.vatModeTarget.value : null;
    const isSingleRate = mode === '5.5' || mode === '10' || mode === '20';
    const tvaMode = this.tvaMode;

    // Select figé en mono-taux (le taux vient du badge), quel que soit le mode de saisie.
    const selectDisabled = isSingleRate ? 'disabled' : '';
    const deleteButtonClass = hideDeleteButton || isSingleRate ? 'hidden' : '';

    // L'input n'est readonly QU'EN saisie HT mono-taux (miroir du montant de base).
    // En saisie TVA, la ligne est l'entrée → éditable même en mono-taux.
    const lineReadonly = isSingleRate && !tvaMode;
    const amountLabel = tvaMode ? 'Montant TVA' : 'Base HT';

    // Générer des IDs uniques pour l'accessibilité (label/input association)
    const uniqueId = crypto.randomUUID();
    const rateSelectId = `vat_rate_${uniqueId}`;
    const amountInputId = `vat_amount_${uniqueId}`;

    return `
      <div data-vat-line class="flex gap-3 items-start p-4 bg-neutral-50 rounded-lg border border-neutral-200" role="listitem" data-testid="vat-rate-row">
        <!-- Select Taux -->
        <div class="flex-1">
          <label for="${rateSelectId}" class="block text-sm font-medium text-neutral-700 mb-2">Taux de TVA</label>
          <select
            id="${rateSelectId}"
            data-vat-rate
            data-action="change->vat-calculator#updateCalculations"
            ${selectDisabled}
            class="block w-full py-2.5 px-3 border border-neutral-300 rounded-lg focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500 sm:text-sm ${selectDisabled ? 'bg-neutral-100 cursor-not-allowed' : 'bg-white'}">
            <option value="">Choisir un taux</option>
            <option value="20" ${preselectedRate === '20' ? 'selected' : ''}>TVA 20%</option>
            <option value="10" ${preselectedRate === '10' ? 'selected' : ''}>TVA 10%</option>
            <option value="5.5" ${preselectedRate === '5.5' ? 'selected' : ''}>TVA 5,5%</option>
          </select>
        </div>

        <!-- Input Montant (HT ou TVA selon le mode) -->
        <div class="flex-1">
          <label for="${amountInputId}" class="block text-sm font-medium text-neutral-700 mb-2">${amountLabel}</label>
          <div class="relative">
            <input
              id="${amountInputId}"
              type="number"
              step="0.01"
              data-vat-amount
              ${lineReadonly ? '' : 'data-action="input->vat-calculator#updateCalculations"'}
              ${lineReadonly ? 'readonly' : ''}
              value="${prefilledAmount !== null ? prefilledAmount : ''}"
              placeholder="0.00"
              class="block w-full py-2.5 pl-3 pr-12 border border-neutral-300 rounded-lg sm:text-sm ${lineReadonly ? 'bg-neutral-100 text-neutral-500 cursor-not-allowed' : 'bg-white focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500'}">
            <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span class="text-neutral-500 sm:text-sm">€</span>
            </div>
          </div>
          ${lineReadonly ? '<p class="mt-1 text-xs text-neutral-500">Synchronisé avec le montant HT ci-dessus</p>' : ''}
          <p data-vat-calculated class="mt-1 text-xs text-primary-600 font-medium invisible">
            TVA calculée : 0,00 €
          </p>
        </div>

        <!-- Bouton Supprimer -->
        <div class="pt-8 ${deleteButtonClass}">
          <button
            type="button"
            aria-label="Supprimer le taux de TVA"
            data-action="click->vat-calculator#removeVatLine"
            class="inline-flex items-center p-2 text-sm font-medium text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-lg transition-colors cursor-pointer">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  // Synchronise les champs cachés avec les lignes visibles
  syncHiddenFields(vatLines) {
    // Réinitialiser tous les champs à 0/false
    if (this.hasVat20ActiveTarget) this.vat20ActiveTarget.value = '0';
    if (this.hasVat10ActiveTarget) this.vat10ActiveTarget.value = '0';
    if (this.hasVat55ActiveTarget) this.vat55ActiveTarget.value = '0';
    if (this.hasAmount20Target) this.amount20Target.value = '0';
    if (this.hasAmount10Target) this.amount10Target.value = '0';
    if (this.hasAmount55Target) this.amount55Target.value = '0';

    // Mettre à jour en fonction des lignes actives
    vatLines.forEach((line) => {
      if (line.rate === 20) {
        if (this.hasVat20ActiveTarget) this.vat20ActiveTarget.value = '1';
        if (this.hasAmount20Target) this.amount20Target.value = line.amountHt;
      } else if (line.rate === 10) {
        if (this.hasVat10ActiveTarget) this.vat10ActiveTarget.value = '1';
        if (this.hasAmount10Target) this.amount10Target.value = line.amountHt;
      } else if (line.rate === 5.5) {
        if (this.hasVat55ActiveTarget) this.vat55ActiveTarget.value = '1';
        if (this.hasAmount55Target) this.amount55Target.value = line.amountHt;
      }
    });
  }

  // Format un nombre en devise française
  formatCurrency(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }
}
